import { useEffect, useState } from 'react';
import axios from 'axios';

import { publicApiRequest } from 'utils/api/request';

export type PublicPostStatus = 'idle' | 'pending' | 'success' | 'error';

export type PublicPostState = {
    status: PublicPostStatus;
    /** the axios error of a failed request (error.response.data is an ApiErrorDto) */
    error: unknown;
};

const IDLE: PublicPostState = { status: 'idle', error: null };
const PENDING: PublicPostState = { status: 'pending', error: null };
const SUCCESS: PublicPostState = { status: 'success', error: null };

export type UsePublicPostParams<TBody> = {
    url: string;
    /** null: nothing to send; every new object is sent once */
    body: TBody | null;
};

/**
 * POST of a form of the signed-out pages to a public endpoint, from an effect: a submit handler sets a new body,
 * the request goes out here. A new body or unmount cancels the request in flight.
 */
export const usePublicPost = <TBody>({ url, body }: UsePublicPostParams<TBody>): PublicPostState => {
    const [state, setState] = useState<PublicPostState>(IDLE);

    useEffect(() => {
        if (body === null) {
            setState(IDLE);

            return undefined;
        }

        const cancelSource = axios.CancelToken.source();
        setState(PENDING);

        publicApiRequest({ method: 'POST', url, data: body }, { cancelToken: cancelSource.token })
            .then(() => {
                // An answer that arrived just before the cancel
                if (!cancelSource.token.reason) {
                    setState(SUCCESS);
                }
            })
            .catch(error => {
                if (axios.isCancel(error) || cancelSource.token.reason) {
                    return;
                }

                setState({ status: 'error', error });
            });

        return () => {
            cancelSource.cancel();
        };
    }, [url, body]);

    return state;
};
